import { useEffect, useState, type FormEvent } from "react";
import { useCreateCategory } from "../hooks/useCategories";
import Modal from "./Modal";

interface CreateCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after the category is created, e.g. so the caller can select it as the active filter. */
  onCreated?: (categoryId: string) => void;
}

export default function CreateCategoryDialog({ open, onClose, onCreated }: CreateCategoryDialogProps) {
  const [name, setName] = useState("");
  const createCategory = useCreateCategory();

  useEffect(() => {
    if (open) {
      setName("");
      createCategory.reset();
    }
    // Intentionally scoped to `open` only — resetting the mutation is a one-time reaction to the
    // dialog opening, not something that should re-run as `createCategory` itself changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    createCategory.mutate(
      { name: trimmed },
      {
        onSuccess: (category) => {
          onCreated?.(category.id);
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="New category">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="new-category-name" className="mb-1 block text-sm font-medium text-text">
            Name
          </label>
          <input
            id="new-category-name"
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Fitness"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted"
          />
        </div>

        {createCategory.isError ? <p className="text-sm text-danger">{createCategory.error.message}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim() || createCategory.isPending}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {createCategory.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
